// @ts-check

import Vue from 'vue'
import Storage from 'vue-web-storage'
import PortalVue from 'portal-vue'
import VueMeta from 'vue-meta'
import VueMask from 'v-mask'
import VueAwesomeSwiper from 'vue-awesome-swiper'
import axios from 'axios'
import UniversalSocialauth from 'universal-social-auth'
import GmapVue, {helpers} from 'gmap-vue'

import App from '@/app-theme/App.vue'
import VueFilters from '@/app-core/filters/index'
import vuetify from '@/plugins/vuetify'
import {resetRouter, router} from '@/router'
import {i18n, initI18nLanguages, setI18nLanguage} from '@/lang/lang'
import DataStorePlugin from '@/app-core/lib/data/dataStore/plugin/DataStorePlugin'
import UtilsPlugin from '@/app-core/lib/utils/plugin'
import EventBus from '@/app-core/lib/events/eventBus'
import Enums from '@/app-core/lib/enums/plugin'
import DayjsPlugin from '@/app-core/lib/date/plugin'
import EnvMixin from '@/app-core/mixins/env'
import LogoMixin from '@/app-core/mixins/logo'
import AuthMixin from '@/app-core/mixins/auth'
import {setupBugsnag} from '@/app-core/lib/services/Bugsnag'
import DataStore from '@/app-core/lib/data/dataStore/DataStore'
import API from '@/app-core/api/Api'
import {sortCompareAlphabetically, uniqueElementsBy} from '@/app-core/lib/utils/array'
import PromiseQueue from '@/app-core/lib/async/PromiseQueue'
import AppConfig from '@/app-core/mixins/appConfig'
import RouteMiddleware from '@/app-core/lib/middleware/RouteMiddleware'
import StoreRouteMiddleware from '@/router/middleware/StoreRouteMiddleware'
import {mergeDeep} from '@/app-core/lib/utils/object'
import CustomerModel from '@/app-core/api/models/customer/CustomerModel'
import {CapacitorService} from '@/app-core/lib/services/Capacitor'
import VuetifyResolver from '@/plugins/vuetifyResolver'
import DefaultLayout from '@/app-theme/layouts/DefaultLayout.vue'
import SimpleLayout from '@/app-theme/layouts/SimpleLayout.vue'
import MapsProviderEnum from '@/app-core/api/enums/MapsProviderEnum'
import MaintenanceModeMiddleware from '@/router/middleware/MaintenanceModeMiddleware'
import RouterReadyMiddleware from '@/router/middleware/RouterReadyMiddleware'
import AppTypeMiddleware from '@/router/middleware/AppTypeMiddleware'
import LangMiddleware from '@/router/middleware/LangMiddleware'
import AuthMiddleware from '@/router/middleware/AuthMiddleware'
import Snotify from '@/app-theme/components/common/vue-snotify/index.js'
import {CookieConsentPlugin} from '@/theme/default/components/common/cookies/CookieConsent.vue'
import '@/app-core/directives/SafeHtml.js'
import '@/app-theme/components/common/cookies/CookieConsentTheme.css'
import '@/app-theme/components/common/vue-snotify/styles/material.css'
import '@openfonts/roboto_greek/index.css'
import 'mapbox-gl/dist/mapbox-gl.css'
import 'v-mapbox/dist/v-mapbox.css'
import 'swiper/css/swiper.css'
import '@mdi/font/css/materialdesignicons.css'
import '@/app-theme/App.css'

const {googleMapsApiInitializer} = helpers
const isSplashScreenAvailable = CapacitorService.isPluginAvailable('SplashScreen')
const isBrowserAvailable = CapacitorService.isPluginAvailable('Browser')

const getSystemConfig = () => {
  const pathArray = window.location.pathname.split('/')
  const langLocale = Array.isArray(pathArray) && pathArray.length > 0 ? pathArray[1] || import.meta.env.VITE_I18N_LOCALE : import.meta.env.VITE_I18N_LOCALE

  API.Resource.Config.Get({}, {headers: {'Accept-Language': langLocale}})
    .then(response => {
      API.isResponseSuccess(response) && init(API.responseData(response))
    })
    .catch(e => {
      console.log(e)
    })
    .finally(() => {
    })
}

const initVue = () => {
  VuetifyResolver()
  resetRouter()

  Vue.component('DefaultLayout', DefaultLayout)
  Vue.component('SimpleLayout', SimpleLayout)

  Vue.mixin(AuthMixin)
  Vue.mixin(EnvMixin)
  Vue.mixin(LogoMixin)
  Vue.mixin(AppConfig)

  Vue.use(CookieConsentPlugin)
  Vue.use(VueFilters)
  Vue.use(EventBus)
  Vue.use(DataStorePlugin)
  Vue.use(UtilsPlugin)
  Vue.use(DayjsPlugin)
  Vue.use(Enums)
  Vue.use(PortalVue)
  Vue.use(VueMask)
  Vue.use(VueMeta, {
    refreshOnceOnNavigation: true
  })

  Vue.use(VueAwesomeSwiper /* { default options with global component } */)

  Vue.use(Snotify, {
    config: {
      timeout        : 3500,
      showProgressBar: true,
      pauseOnHover   : true,
      titleMaxLength : 28,
      bodyMaxLength  : 150,
      backdrop       : -1
    },
    options: {
      maxOnScreen  : 3,
      maxAtPosition: 8,
      maxHeight    : 300,
      newOnTop     : true,
      position     : 'centerTop'
    }
  })

  Vue.use(Storage, {
    prefix : `${import.meta.env.VITE_STORAGE_PREFIX}_`,
    drivers: ['session', 'local']
  })

  const options = {
    providers: {
      ...DataStore.App.Config.Auth.Google && {google: DataStore.App.Config.Auth.Providers.google},
      ...DataStore.App.Config.Auth.Facebook && {facebook: DataStore.App.Config.Auth.Providers.facebook}
    }
  }
  const Oauth = new UniversalSocialauth(axios, options)
  Vue.prototype.$axios = axios
  Vue.prototype.$Oauth = Oauth

  window.Vue = new Vue({
    el        : '#app',
    directives: {},
    mixins    : [],
    data() {
      return {}
    },
    beforeMount() {
      setI18nLanguage()
      this.initCoreRouteGuards()
    },
    beforeCreate() {
    },
    created() {
      if (this.user) {
        this.user.AuthStatus()
          .then(response => {
            const data = API.responseData(response) || {}
            if (API.isResponseSuccess(response)) {
              if (data.IsLogged && data.CustomerInfo) {
                this.user = new CustomerModel(data.CustomerInfo)
              } else {
                this.logout()
              }
            }
          })
          .catch(e => {
            this.logout()
          })
      }

      if (CapacitorService.isNative) {
        this.$dataStore.App.Type.isMobileApp = true
        if (isSplashScreenAvailable) CapacitorService.Plugins.SplashScreen.hide()
        document.addEventListener('click', interceptClickEvent)
      }
    },
    mounted() {
      this.$bus.$on('app:logout', this.logout)
    },
    beforeDestroy() {
      this.$bus.$off('app:logout', this.logout)
    },
    methods: {
      initCoreRouteGuards() {
        this.$router.onReady(() => {
          MaintenanceModeMiddleware(this.$route, null, null, this)
          RouterReadyMiddleware(this.$route, this)
          AppTypeMiddleware(this.$route, this)
        })

        router.beforeEach(RouteMiddleware({
          LangMiddleware,
          MaintenanceModeMiddleware,
          AuthMiddleware
        }, true, this))

        router.afterEach(RouteMiddleware({
          StoreRouteMiddleware
        }, true, this))
      }
    },
    render: h => h(App),
    i18n,
    router,
    vuetify
  })

  if (import.meta.env.MODE === 'production') {
    Vue.config.productionTip = false
    Vue.config.devtools = false
    Vue.config.debug = false
    Vue.config.silent = true
  }
}

const init = (data = {}) => {
  window.CartPromiseQueue = new PromiseQueue({
    eventPrefix: 'cart:queue',
    concurrency: 1,
    cooldown   : 0
  })

  try {
    DataStore.App.Config = mergeDeep((DataStore?.App?.Config ?? {}), (data?.Settings ?? {}))
  } catch (e) {
    if (data?.Settings) DataStore.App.Config = data.Settings
  }

  DataStore.App.QrItems = (data?.QrItems || [])
  DataStore.App.Tags = (data?.Tags || [])
  DataStore.App.Stores = (data?.Stores || []).sort(sortCompareAlphabetically('Name'))
  DataStore.App.Categories = uniqueElementsBy(DataStore.App.Stores.map(store => store.Category), (a, b) => a.Id === b.Id)
  DataStore.App.Payments = data?.Payments || []
  DataStore.App.Services = data?.Services || []
  DataStore.App.Cart.PaymentMethod = DataStore.App.Payments.find(payment => payment.Status)

  localStorage.setItem('x-session', data?.Settings?.XSession || '')

  initMaps()
  initVue()
  setupBugsnag()

  //console.log('ORDERID', HmacMD5.hash('ORDERID'))
}

const initMaps = () => {

  // Start - Init Google Maps
  const providers = DataStore?.App?.Config?.Maps || {}
  const providersArray = Object.values(providers)
  const googleProvider = providersArray?.find(provider => provider.Name === MapsProviderEnum.Google && provider.Key)
  const googleMapsKey = googleProvider?.Key || ''

  if (googleProvider && googleMapsKey) {
    googleMapsApiInitializer({
      key      : googleMapsKey,
      libraries: 'places,geometry,visualization',
      language : i18n.locale || import.meta.env.VITE_I18N_LOCALE
      // v        : 3.38
    }, false)

    Vue.use(GmapVue)
  }
  // End - Init Google Maps
}

const interceptClickEvent = (e) => {
  let href
  if (e.target.tagName === 'A') {
    href = e.target.getAttribute('href')
    if (CapacitorService.isNative && isBrowserAvailable && !href.includes(window.location.hostname)) {
      e.preventDefault()
      CapacitorService.Plugins.Browser.open({url: href})
    }
  }
}

initI18nLanguages()
getSystemConfig()
